/* eslint-disable react/prop-types */
import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@helpers/styleUtils';

const MultiSelect = React.forwardRef(
    ({ className, value = [], onChange, options, placeholder = 'Select multiple', ...props }, ref) => {
        const [open, setOpen] = React.useState(false);

        // Ensure options always has a valid value
        const sanitizedOptions = React.useMemo(() => {
            return options
                .map(option => ({
                    ...option,
                    value: option.value ?? option.label,
                    label: option.label ?? option.value,
                }))
                .filter(option => option.value !== '');
        }, [options]);

        const handleSelect = (selectedValue) => {
            if (selectedValue === '') {
                // "All" option - clear all selections
                onChange([]);
                return;
            }

            const newValue = value.includes(selectedValue)
                ? value.filter(v => v !== selectedValue)
                : [...value, selectedValue];

            onChange(newValue);
        };

        const handleRemove = (valueToRemove) => {
            const newValue = value.filter(v => v !== valueToRemove);
            onChange(newValue);
        };

        const handleSelectAll = () => {
            const allValues = sanitizedOptions.filter(opt => opt.value !== '').map(opt => opt.value);
            const isAllSelected = allValues.every(val => value.includes(val));

            if (isAllSelected) {
                onChange([]);
            } else {
                onChange(allValues);
            }
        };

        const isAllSelected = sanitizedOptions.filter(opt => opt.value !== '').every(opt => value.includes(opt.value));

        return (
            <div className="relative">
                <DropdownMenu.Root open={open} onOpenChange={setOpen}>
                    <DropdownMenu.Trigger asChild>
                        <button
                            ref={ref}
                            className={cn(
                                'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                                className,
                            )}
                            {...props}
                        >
                            <div className="flex items-center flex-wrap gap-1 flex-1">
                                {value.length === 0 ? (
                                    <span className="text-muted-foreground">{placeholder}</span>
                                ) : value.length === 1 ? (
                                    <span>{sanitizedOptions.find(opt => opt.value === value[0])?.label || value[0]}</span>
                                ) : (
                                    <div className="flex items-center gap-1">
                                        {value.slice(0, 2).map((val) => {
                                            const option = sanitizedOptions.find(opt => opt.value === val);
                                            return (
                                                <span
                                                    key={val}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-secondary text-secondary-foreground rounded-md"
                                                >
                                                    {option?.label || val}
                                                    <X
                                                        className="h-3 w-3 cursor-pointer hover:text-destructive"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemove(val);
                                                        }}
                                                    />
                                                </span>
                                            );
                                        })}
                                        {value.length > 2 && (
                                            <span className="text-xs text-muted-foreground">
                                                +{value.length - 2} more
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                        </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.Content
                            className="z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-80 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
                            align="start"
                            sideOffset={5}
                        >
                            {/* Select All / Clear All option */}
                            <DropdownMenu.Item
                                className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSelectAll();
                                }}
                            >
                                <div className="flex items-center space-x-2">
                                    <div className="flex h-4 w-4 items-center justify-center border border-primary rounded-sm">
                                        {isAllSelected && <Check className="h-3 w-3" />}
                                    </div>
                                    <span className="font-medium">
                                        {isAllSelected ? 'Clear All' : 'Select All'}
                                    </span>
                                </div>
                            </DropdownMenu.Item>

                            <DropdownMenu.Separator className="my-1 h-px bg-muted" />

                            {sanitizedOptions.filter(opt => opt.value !== '').map((option) => (
                                <DropdownMenu.Item
                                    key={option.value}
                                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSelect(option.value);
                                    }}
                                >
                                    <div className="flex items-center space-x-2">
                                        <div className="flex h-4 w-4 items-center justify-center border border-primary rounded-sm">
                                            {value.includes(option.value) && (
                                                <Check className="h-3 w-3" />
                                            )}
                                        </div>
                                        <span>{option.label}</span>
                                    </div>
                                </DropdownMenu.Item>
                            ))}
                        </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            </div>
        );
    },
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect }; 